import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';

import { useEffect, useRef, useState, useCallback } from 'react';
import { fetchCompanyOverviewData } from '../../../../store/dashboard/overview/actions';
import { OverviewData } from '../../../../store/dashboard/overview/types';
import { initialOverviewData } from '../../../../store/dashboard/overview/constants';
import styles from './Overview.module.scss';
import { ACCESS_TIERS } from '../../../../store/investor/constants';

import { DATA_FETCH_STATUS } from '../../../../store/dashboard/constants';
import axios from 'axios';
import { changeOrgIdHeaderForCharts } from '../../../../utils/axios-interceptor';
import { setDashboardConfig } from '../../../../store/dashboard/actions';

import Details from './components/Details/Details';
import KeyInvestors2 from './components/KeyInvestors/KeyInvestors';
import NewsUpdates2 from './components/NewsUpdates/NewsUpdates';
import SectorIndustryOutlook from './components/SectorIndustryOutlook/SectorIndustryOutlook';
import KeyCompetitors2 from './components/KeyCompetitors/KeyCompetitors';
import EmployeeAndWebsiteTraffic from './components/EmployeeAndWebsiteTraffic/EmployeeAndWebsiteTraffic';
import Checks2 from './components/Checks/Checks';
import Users from './components/UserAndUserSubmittedDetails/Users';
import GeneratingReport from './components/GeneratingReport/GeneratingReport';
import clsx from 'clsx';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { REPORT_SECTION_NAMES } from '../../../../store/pdfReport/pdfReport_constants';
import AlertEngineTable from '../../components/AlertEngine/components/AlertEngineTable/AlertEngineTable';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { getCompanyOverviewDataV2 } from '../../../../store/dashboard/overview/service';
import { setCompanyLogoWebsite, setEnrichedLinkedin } from '../../../../store/investor/action';
import { linkedInRegex } from '../../../../utils/utils';
import { findSubjectCompanyWebsite, pickWebsite } from '../../../../utils/urlMetadata';
import NewsUpdatesV2 from './components/NewsUpdatesV2/NewsUpdatesV2';
import SectorIndustryOutlookV2 from './components/SectorIndustryOutlookV2/SectorIndustryOutlookV2';
import KeyCompetitorsV2 from './components/KeyCompetitorsV2/KeyCompetitorsV2';
import A4Page from '../../../AicaPDFReport/A4Page/A4Page';
import { usePolling } from '../../../../utils/commonUtil';
import { fetchOrgInfoById } from '../../../../store/investor/investorApiHelper';
import { isEmpty, isAllKeysEmpty } from '../../../../utils/utils';
import FundingHistoryV2 from './components/FundingHistoryV2/FundingHistoryV2';
import CreditReport from './components/CreditReport/CreditReport';
import AICAGradeV2 from './components/AICAGradeV2/AICAGradeV2';
import { TabModule } from '../../../../utils/constants/commonConstants';
import { useTabLocked } from '../../../../common/hooks/useTabLocked';
import usePrevious from '../../../../common/_custom/Hooks/usePrevious';

function Overview({
    selectedCompany,
    refetchOverviewRef,
    appendSearchParams,
    navigateToTab,
    isPDFReport = false,
    triggerMain = false,
}: {
    selectedCompany: any;
    refetchOverviewRef: any;
    appendSearchParams: any;
    navigateToTab?: (tabId: string, additionalParams?: Record<string, string>) => void;
    isPDFReport?: boolean;
    triggerMain?: boolean;
}) {
    const { id } = useParams();
    const dispatch = useAppDispatch();
    const userData = useAppSelector((state) => state.user.userData);
    const location = useLocation();
    const dashboardConfig = useAppSelector((state) => state.dashboard.dashboardReducer.config);
    const isNewOverviewEnabled =
        useAppSelector((state) => state.dashboard.dashboardReducer.config?.tabConfigs?.overview?.showNewOverview) || false;
    const isMcaFilingLocked = useTabLocked(TabModule.MCA_FILING);
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);

    const [loadingOverview, setLoadingOverview] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [overviewData, setOverviewData] = useState<OverviewData>(initialOverviewData);
    const [showGeneratingPill, setShowGeneratingPill] = useState(false);
    const [showUsersDetails, setShowUsersDetails] = useState(false);
    const [overviewDataV2, setOverviewDataV2] = useState<any>(null);
    const [isPollingOverviewV2, setIsPollingOverviewV2] = useState(false);
    const [isOverviewV2Failed, setIsOverviewV2Failed] = useState(false);

    const accessType = selectedCompany?.companyAccessType || '';
    const isOverviewLocked = overviewData?.overallStatus === 'LOCKED';

    const isGeneratingProfile = overviewData?.overallStatus === DATA_FETCH_STATUS.PROCESSING && !isOverviewLocked;

    let pollingIntervalRef: any = useRef(null);
    const assesseeOrgId = id ?? selectedCompany.assesseeOrgId;

    // Check for refresh param
    useEffect(() => {
        const refreshOverviewV2 = searchParams.get('refreshOverviewV2') === 'true';

        if (refreshOverviewV2) {
            // Reset overviewDataV2 and set polling flag
            setOverviewDataV2(null);
            setIsOverviewV2Failed(false);
            setIsPollingOverviewV2(true);

            // Remove the param after processing
            searchParams.delete('refreshOverviewV2');
            updateQueryParams(searchParams, setSearchParams, {}, location);
        }
    }, [searchParams, setSearchParams, location]);

    // Use the polling hook for getCompanyOverviewDataV2
    const fetchOverviewData = useCallback(async () => {
        if (!assesseeOrgId || !userData.investorOrgId) {
            return null;
        }
        const response = await getCompanyOverviewDataV2({
            assesseeOrgId: assesseeOrgId,
        });
        return response?.data?.responseBody?.data;
    }, [assesseeOrgId, userData.investorOrgId]);

    const handlePollingSuccess = useCallback(
        (data) => {
            if (!data) return;

            setOverviewDataV2(data);
            setIsPollingOverviewV2(false);
            // Share the company's own website into Redux so distant surfaces like the
            // report nav bar render the SAME logo as the Details card. Priority must
            // match Details.tsx exactly: socialInfo.websiteUrl first, then the AI
            // "Target company" competitor website — otherwise the two can diverge.
            dispatch(
                setCompanyLogoWebsite(
                    pickWebsite(
                        overviewData?.socialInfo?.websiteUrl,
                        findSubjectCompanyWebsite(data?.competitors, selectedCompany?.orgName),
                    ),
                ),
            );
            // Share the enriched LinkedIn so the Edit Profile drawer can pre-fill its
            // field without refetching. Gated on linkedInRegex because the drawer's own
            // validateLinkedIn runs on hydration — seeding a value it rejects (enrichment
            // emits sentinels like "Data Not Found") would disable both footer buttons.
            const enriched = (data?.overview?.socials ?? []).find((s) => s?.platform?.trim().toLowerCase() === 'linkedin')?.url;
            const enrichedTrimmed = enriched?.trim() || '';
            dispatch(setEnrichedLinkedin(linkedInRegex.test(enrichedTrimmed) ? enrichedTrimmed : ''));
        },
        [dispatch, selectedCompany?.orgName, overviewData?.socialInfo?.websiteUrl],
    );

    // Clear the shared logo website + enriched LinkedIn and the V2 overview state when switching
    // companies so no stale value leaks into the next company's nav bar, Edit Profile form or cards.
    useEffect(() => {
        dispatch(setCompanyLogoWebsite(''));
        dispatch(setEnrichedLinkedin(''));
        setOverviewDataV2(null);
        setIsOverviewV2Failed(false);
    }, [assesseeOrgId, dispatch]);

    const shouldContinuePolling = useCallback(
        (data) => {
            // Continue polling if data is null or isPollingOverviewV2 flag is true
            return !data || isPollingOverviewV2;
        },
        [isPollingOverviewV2],
    );

    const handlePollingError = useCallback(() => {
        setIsOverviewV2Failed(true);
        setIsPollingOverviewV2(false);
    }, []);

    usePolling({
        fetchData: fetchOverviewData,
        onSuccess: handlePollingSuccess,
        shouldContinue: shouldContinuePolling,
        interval: 5000, // Poll every 5 seconds
        onError: handlePollingError,
    });

    useEffect(() => {
        if (isGeneratingProfile) {
            setTimeout(() => {
                setShowGeneratingPill(true);
            }, 2000);
        } else {
            setShowGeneratingPill(false);
        }
    }, [isGeneratingProfile]);

    useEffect(() => {
        if (assesseeOrgId) {
            changeOrgIdHeaderForCharts(axios, assesseeOrgId);
            getCompanyData();
            fetchPartnerOrgInfo();
        }
    }, [assesseeOrgId]);

    const fetchPartnerOrgInfo = async () => {
        try {
            const orgInfo = await fetchOrgInfoById({ orgId: assesseeOrgId });
            const partnerOrgId = orgInfo?.customFields?.partnerOrgId || '';

            if (isEmpty(partnerOrgId)) return;

            updateQueryParams(searchParams, setSearchParams, { partnerOrgId: partnerOrgId }, location);
        } catch (error) {
            console.error('Error fetching partner organization info:', error);
        }
    };

    useEffect(() => {
        if (refetchOverviewRef?.current) {
            getCompanyData(false, true);
        }
    }, [refetchOverviewRef?.current]);

    useEffect(() => {
        if (!isPDFReport && overviewData?.additionalDetails) {
            setShowUsersDetails(Object.values(overviewData.additionalDetails).some(({ value }) => !!value));
        }
    }, [overviewData?.additionalDetails, isPDFReport]);

    useEffect(() => {
        if (isGeneratingProfile && !isPDFReport) {
            pollingIntervalRef.current = setInterval(() => {
                getCompanyData(true);
            }, 5000);
        } else {
            clearInterval(pollingIntervalRef?.current);
        }

        return () => {
            clearInterval(pollingIntervalRef?.current);
        };
    }, [isGeneratingProfile]);

    useEffect(() => {
        if (triggerMain || searchParams.get('triggerMain') === 'true') {
            const noLoading = searchParams.get('noLoading') === 'true';
            getCompanyData(noLoading); // Pass noLoading as isRepeatPollingCall to prevent loading state
            searchParams.delete('triggerMain');
            if (noLoading) searchParams.delete('noLoading');
            updateQueryParams(searchParams, setSearchParams, {}, location);
        }
    }, [triggerMain, searchParams.get('triggerMain')]);

    const previousModulePollingList = usePrevious(modulePollingList);
    const currentAssesseeOrgIdRef = useRef(assesseeOrgId);
    currentAssesseeOrgIdRef.current = assesseeOrgId;
    const mcaRefetchGenerationRef = useRef(0);

    /** Rejects every in-flight MCA refetch; a company switch calls it so a switch back to the same org cannot revive one. */
    const invalidateMcaRefetches = () => {
        mcaRefetchGenerationRef.current += 1;
    };

    /** Credit ratings are written by the MCA Filings ingestion, so refetch V2 once that refresh drains. */
    const refetchOverviewIfMcaFilingDrained = () => {
        const mcaFilingRefreshDrained =
            previousModulePollingList?.includes(TabModule.MCA_FILING) && !modulePollingList.includes(TabModule.MCA_FILING);
        if (isPDFReport || !mcaFilingRefreshDrained) return;
        const requestedAssesseeOrgId = assesseeOrgId;
        invalidateMcaRefetches();
        const generation = mcaRefetchGenerationRef.current;
        fetchOverviewData()
            .then((data) => {
                // Only the latest drain's refetch may land: an earlier one resolving late carries older ratings.
                const isLatest = generation === mcaRefetchGenerationRef.current;
                if (isLatest && requestedAssesseeOrgId === currentAssesseeOrgIdRef.current) handlePollingSuccess(data);
            })
            .catch((error) => console.error('Error refetching overview after MCA Filings refresh:', error));
    };

    useEffect(() => {
        invalidateMcaRefetches();
    }, [assesseeOrgId]);

    useEffect(() => {
        refetchOverviewIfMcaFilingDrained();
    }, [modulePollingList]);

    const getCompanyData = (isRepeatPollingCall = false, isCallFromOutside = false) => {
        !isRepeatPollingCall && setLoadingOverview(true);
        dispatch(
            fetchCompanyOverviewData({
                assesseeOrgId: assesseeOrgId,
                investorOrgId: userData.investorOrgId,
                partnerUserId: userData.userId,
                onSuccess: (data: OverviewData, actionCta) => {
                    !isRepeatPollingCall && setLoadingOverview(false);
                    if (isCallFromOutside) refetchOverviewRef.current = false;
                    setOverviewData((prevData) => data || prevData);
                    dispatch(setDashboardConfig({ ...dashboardConfig, actionCta }));
                },
            }),
        );
    };

    const renderNews = () =>
        isNewOverviewEnabled && overviewDataV2?.newsReport?.[0] != null && !isAllKeysEmpty(overviewDataV2.newsReport[0]) ? (
            <NewsUpdatesV2 newsReport={overviewDataV2.newsReport[0]} />
        ) : (
            <NewsUpdates2 newsInfo={overviewData.newsInfo} accessType={accessType} isPDFReport={isPDFReport} />
        );

    const renderSectorOutlook = () =>
        isNewOverviewEnabled && overviewDataV2?.sectorOutlook != null && !isAllKeysEmpty(overviewDataV2.sectorOutlook) ? (
            <SectorIndustryOutlookV2 sectorOutlook={overviewDataV2.sectorOutlook} sectorName={overviewDataV2.sectorDetails?.sector} />
        ) : (
            <SectorIndustryOutlook sectorInfo={overviewData.sectorInfo} accessType={accessType} isPDFReport={isPDFReport} />
        );

    const renderKeyCompetitors = () =>
        isNewOverviewEnabled && !isEmpty(overviewDataV2?.competitors) ? (
            <KeyCompetitorsV2 competitors={overviewDataV2.competitors} isPDFReport={isPDFReport} />
        ) : (
            <KeyCompetitors2 competitorsInfo={overviewData.competitorsInfo} isPDFReport={isPDFReport} />
        );

    const renderSectorOutlookNewsAndKeyCompetitors = () => {
        return isPDFReport ? (
            <>
                <A4Page sectionName={REPORT_SECTION_NAMES.NEWS_UPDATES}>{renderNews()}</A4Page>
                <A4Page sectionName={REPORT_SECTION_NAMES.SECTOR_OUTLOOK}>{renderSectorOutlook()}</A4Page>
                <A4Page sectionName={REPORT_SECTION_NAMES.KEY_COMPETITORS}>{renderKeyCompetitors()}</A4Page>
            </>
        ) : (
            <>
                {renderNews()}
                {renderSectorOutlook()}
                {renderKeyCompetitors()}
            </>
        );
    };

    if (loadingOverview) {
        return (
            <div>
                <LoadingSpinner color={'var(--primary-text-colour)'} height={'35px'} style={{ marginTop: '50px' }} />
            </div>
        );
    }

    const creditReport = isNewOverviewEnabled && (
        <CreditReport
            isPDFReport={isPDFReport}
            probeCreditRating={overviewDataV2?.probeCreditRating ?? null}
            companyName={selectedCompany?.orgName}
            isLoading={overviewDataV2 === null && !isOverviewV2Failed}
            isMcaFilingLocked={isMcaFilingLocked}
        />
    );

    const employeeAndWebsite = !isOverviewLocked ? (
        <EmployeeAndWebsiteTraffic
            employeeData={overviewData?.employeeData}
            websiteTraffic={overviewData?.websiteTraffic}
            isLoading={loadingOverview}
            isPDFReport={isPDFReport}
        />
    ) : null;

    // Screen layout: AI-generated sections go into the left bento column,
    // Employees & Website Traffic go into the right bento column.
    // The PDF report keeps rendering these as full-width siblings below.
    const bentoLeftSlot = !isPDFReport ? (
        <>
            {renderNews()}
            {creditReport}
            {renderSectorOutlook()}
            {renderKeyCompetitors()}
        </>
    ) : undefined;

    const bentoRightSlot = !isPDFReport ? employeeAndWebsite : undefined;

    return (
        <div
            className={clsx({
                [styles.Overview]: !isPDFReport,
                [styles.Loading]: loadingOverview,
            })}
        >
            {showGeneratingPill && !isPDFReport && <GeneratingReport />}
            <Details
                overviewData={overviewData}
                accessType={accessType}
                selectedCompany={selectedCompany}
                isPDFReport={isPDFReport}
                overviewDataV2={overviewDataV2}
                leftSlot={bentoLeftSlot}
                rightSlot={bentoRightSlot}
                navigateToTab={navigateToTab}
            />

            {isPDFReport && renderSectorOutlookNewsAndKeyCompetitors()}
            {isNewOverviewEnabled &&
                isOverviewLocked &&
                overviewDataV2?.funding?.fundingSummary != null &&
                overviewDataV2?.funding?.fundingSummary != '' && (
                    <FundingHistoryV2 isPDFReport={isPDFReport} fundingData={overviewDataV2?.funding} />
                )}
            {isPDFReport && creditReport}

            {isPDFReport && !isOverviewLocked && accessType !== ACCESS_TIERS.WATCH && <AICAGradeV2 isPDFReport={isPDFReport} />}
            {isPDFReport && !isOverviewLocked && (
                <>
                    <AlertEngineTable
                        parentOrgId={userData.investorOrgId}
                        assesseOrgId={selectedCompany?.assesseeOrgId}
                        isPDFReport={isPDFReport}
                        assesseeDisplayName={selectedCompany?.orgName}
                    />
                    {employeeAndWebsite}
                </>
            )}
            <KeyInvestors2 fundingInfo={overviewData?.fundingInfo} isPDFReport={isPDFReport} />

            {accessType !== ACCESS_TIERS.WATCH && !isOverviewLocked && (
                <Checks2 isLoading={loadingOverview} isGeneratingProfile={isGeneratingProfile} isPDFReport={isPDFReport} />
            )}

            {showUsersDetails && (
                <Users
                    additionalDetails={overviewData?.additionalDetails}
                    additionalDocMetaData={overviewData?.additionalDocMetaData}
                    selectedCompany={selectedCompany}
                />
            )}
        </div>
    );
}

export default Overview;
